// src/components/ImagesTab.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { ClipboardIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { FaImage } from "react-icons/fa";

// In-memory module cache for fast tab-switching during session
const memoryImageCache = new Map(); // key: `${topic}:${imageName}` -> { blob: Blob, url: string }

const getInitialOpenImages = (topic) => {
  if (!topic) return [];
  try {
    const saved = localStorage.getItem(`codetyper_open_images_${topic}`);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error("Failed to load open images from localStorage", e);
  }
  return [];
};

const ImagesTab = ({ selectedTopic }) => {
  const [images, setImages] = useState([]);
  const [openImages, setOpenImages] = useState(() => getInitialOpenImages(selectedTopic));
  const [imageUrlsMap, setImageUrlsMap] = useState({});
  const [imageBlobsMap, setImageBlobsMap] = useState({});
  const [imageLoadingMap, setImageLoadingMap] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [copyStatusMap, setCopyStatusMap] = useState({});

  const backendURL = process.env.REACT_APP_BACKEND_URL;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // When selectedTopic changes, restore that topic's open images and cached URLs
  useEffect(() => {
    const initialOpen = getInitialOpenImages(selectedTopic);
    setOpenImages(initialOpen);

    const initialUrls = {};
    const initialBlobs = {};
    initialOpen.forEach((img) => {
      const cacheKey = `${selectedTopic}:${img}`;
      if (memoryImageCache.has(cacheKey)) {
        const cached = memoryImageCache.get(cacheKey);
        initialUrls[img] = cached.url;
        initialBlobs[img] = cached.blob;
      }
    });

    setImageUrlsMap(initialUrls);
    setImageBlobsMap(initialBlobs);
    setImageLoadingMap({});
  }, [selectedTopic]);

  // Persist openImages in localStorage per topic
  useEffect(() => {
    if (!selectedTopic) return;
    try {
      localStorage.setItem(
        `codetyper_open_images_${selectedTopic}`,
        JSON.stringify(openImages)
      );
    } catch (e) {
      console.error("Failed to save open images to localStorage", e);
    }
  }, [openImages, selectedTopic]);

  const fetchImages = useCallback(async () => {
    if (!selectedTopic) return;
    try {
      setLoading(true);
      setMessage("Fetching images...");
      const response = await axios.post(`${backendURL}/list_images`, {
        topic: selectedTopic,
      });

      if (response.status === 200 && isMountedRef.current) {
        const extractSortableParts = (filename) => {
          const lastDotIndex = filename.lastIndexOf(".");
          const nameWithoutExtension =
            lastDotIndex !== -1 ? filename.slice(0, lastDotIndex) : filename;
          const extension = lastDotIndex !== -1 ? filename.slice(lastDotIndex) : "";

          const match = nameWithoutExtension.match(/^(.*?)(?:_(\d+))?$/);

          return {
            base: match ? match[1] : nameWithoutExtension,
            number: match && match[2] ? parseInt(match[2], 10) : null,
            extension,
          };
        };

        const sortedImages = [...response.data.images].sort((a, b) => {
          const aParts = extractSortableParts(a);
          const bParts = extractSortableParts(b);

          const baseComparison = bParts.base.localeCompare(aParts.base, undefined, {
            sensitivity: "base",
          });
          if (baseComparison !== 0) {
            return baseComparison;
          }

          if (aParts.number !== null && bParts.number !== null && aParts.number !== bParts.number) {
            return bParts.number - aParts.number;
          }

          if (aParts.number !== null && bParts.number === null) {
            return -1;
          }

          if (aParts.number === null && bParts.number !== null) {
            return 1;
          }

          const extensionComparison = bParts.extension.localeCompare(
            aParts.extension,
            undefined,
            { sensitivity: "base" }
          );
          if (extensionComparison !== 0) {
            return extensionComparison;
          }

          return b.localeCompare(a, undefined, { sensitivity: "base" });
        });

        setImages(sortedImages);
        setMessage(
          response.data.images.length
            ? "Images fetched successfully."
            : "No images available."
        );
      } else if (isMountedRef.current) {
        setMessage("Failed to fetch images.");
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error(error);
        setMessage("Error fetching images.");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [backendURL, selectedTopic]);

  // Load a single image from cache or network
  const loadImage = useCallback(
    async (imageName) => {
      const cacheKey = `${selectedTopic}:${imageName}`;

      // 1. Check in-memory module cache
      if (memoryImageCache.has(cacheKey)) {
        const cached = memoryImageCache.get(cacheKey);
        setImageUrlsMap((prev) => ({ ...prev, [imageName]: cached.url }));
        setImageBlobsMap((prev) => ({ ...prev, [imageName]: cached.blob }));
        return;
      }

      // 2. Fetch from backend
      try {
        setImageLoadingMap((prev) => ({ ...prev, [imageName]: true }));
        const response = await axios.post(
          `${backendURL}/get_image`,
          {
            filename: imageName,
            topic: selectedTopic,
          },
          { responseType: "blob" }
        );

        if (response.status === 200 && isMountedRef.current) {
          const blob = response.data;
          const url = URL.createObjectURL(blob);
          memoryImageCache.set(cacheKey, { blob, url });

          setImageUrlsMap((prev) => ({ ...prev, [imageName]: url }));
          setImageBlobsMap((prev) => ({ ...prev, [imageName]: blob }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error(`Error loading image ${imageName}:`, error);
        }
      } finally {
        if (isMountedRef.current) {
          setImageLoadingMap((prev) => ({ ...prev, [imageName]: false }));
        }
      }
    },
    [backendURL, selectedTopic]
  );

  // Toggle image dropdown
  const toggleImage = (imageName) => {
    const isCurrentlyOpen = openImages.includes(imageName);

    if (isCurrentlyOpen) {
      // Close this specific image
      setOpenImages((prev) => prev.filter((img) => img !== imageName));
    } else {
      // Open this specific image
      setOpenImages((prev) => [...prev, imageName]);

      const cacheKey = `${selectedTopic}:${imageName}`;
      if (memoryImageCache.has(cacheKey)) {
        const cached = memoryImageCache.get(cacheKey);
        setImageUrlsMap((prev) => ({ ...prev, [imageName]: cached.url }));
        setImageBlobsMap((prev) => ({ ...prev, [imageName]: cached.blob }));
      } else {
        loadImage(imageName);
      }
    }
  };

  // If there are already open images on mount/topic change, load any uncached ones
  useEffect(() => {
    openImages.forEach((img) => {
      const cacheKey = `${selectedTopic}:${img}`;
      if (memoryImageCache.has(cacheKey)) {
        const cached = memoryImageCache.get(cacheKey);
        setImageUrlsMap((prev) => ({ ...prev, [img]: cached.url }));
        setImageBlobsMap((prev) => ({ ...prev, [img]: cached.blob }));
      } else {
        loadImage(img);
      }
    });
  }, [openImages, selectedTopic, loadImage]);

  const copyToClipboard = async (imageName, blob) => {
    if (!blob) return;
    try {
      const clipboardItem = new ClipboardItem({ "image/png": blob });
      await navigator.clipboard.write([clipboardItem]);
      setCopyStatusMap((prev) => ({ ...prev, [imageName]: "Copied" }));
      setTimeout(() => {
        setCopyStatusMap((prev) => ({ ...prev, [imageName]: "Copy Image" }));
      }, 1200);
    } catch (error) {
      console.error("Failed to copy image:", error);
      setMessage("Failed to copy image.");
    }
  };

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  return (
    <div className="relative flex justify-center min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white pb-16">
      {/* Images Container */}
      <div className="w-full max-w-4xl bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 space-y-6 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200">
            Images
          </h2>
          {openImages.length > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium border border-indigo-200 dark:border-indigo-800">
              {openImages.length} open
            </span>
          )}
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="flex justify-center items-center">
            <svg
              className="animate-spin h-8 w-8 text-blue-500 dark:text-blue-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-label="Loading spinner"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              ></path>
            </svg>
          </div>
        )}

        {/* Images List with Multi-Accordion */}
        <div id="accordion-collapse">
          {images.map((image, index) => {
            const isOpen = openImages.includes(image);
            const imageUrl = imageUrlsMap[image];
            const imageBlob = imageBlobsMap[image];
            const isImageLoading = Boolean(imageLoadingMap[image]);
            const copyStatus = copyStatusMap[image] || "Copy Image";

            return (
              <div
                key={image}
                className="rounded-lg overflow-hidden shadow-md mb-4 border border-gray-200 dark:border-gray-700"
              >
                <h2 id={`accordion-collapse-heading-${index}`}>
                  <button
                    type="button"
                    className="flex items-center justify-between w-full p-5 font-medium text-left text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-b-0 border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    onClick={() => toggleImage(image)}
                    aria-expanded={isOpen}
                    aria-controls={`accordion-collapse-body-${index}`}
                  >
                    <div className="flex items-center space-x-2">
                      <FaImage className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                      <span>{image}</span>
                      {isImageLoading && (
                        <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin ml-2" />
                      )}
                    </div>
                    <svg
                      className={`w-5 h-5 transition-transform duration-200 ${
                        isOpen ? "transform rotate-180" : ""
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </h2>
                {isOpen && (
                  <div
                    id={`accordion-collapse-body-${index}`}
                    className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/80 rounded-b-lg"
                    aria-labelledby={`accordion-collapse-heading-${index}`}
                  >
                    <div className="relative">
                      {/* Copy Button */}
                      <button
                        onClick={() => copyToClipboard(image, imageBlob)}
                        disabled={isImageLoading || !imageBlob}
                        className="absolute top-2 right-2 px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 flex items-center transition-colors disabled:opacity-50 z-10 shadow-md"
                        aria-label="Copy image to clipboard"
                      >
                        <ClipboardIcon className="w-4 h-4 mr-1" />
                        {copyStatus}
                      </button>

                      {/* Image Display */}
                      {isImageLoading ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
                          <ArrowPathIcon className="w-5 h-5 animate-spin text-blue-500" />
                          <span>Loading image...</span>
                        </div>
                      ) : imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={image}
                          className="max-w-full h-auto rounded-md shadow-md block"
                        />
                      ) : (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                          Failed to load image.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Message Display */}
        {message && !loading && (
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            {message}
          </div>
        )}
      </div>

      {/* Floating Refresh Button */}
      <button
        type="button"
        onClick={fetchImages}
        disabled={loading}
        className="fixed top-16 right-6 md:top-20 md:right-10 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
        aria-label="Refresh images"
      >
        <ArrowPathIcon className={`h-6 w-6 ${loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
};

export default ImagesTab;
